/**
 * Manual (non-catalog) bill lines.
 *
 * ── The need ────────────────────────────────────────────────────────────────
 * A counter sells things that are not in the catalog: a one-off fitting, a
 * labour charge, an item bought in that morning. Making the shopkeeper create
 * a product first — name, SKU, purchase price, stock — just to put a line on
 * one bill is the wrong trade, and it fills the catalog with entries nobody
 * will ever sell again.
 *
 * ── Why a placeholder product exists ────────────────────────────────────────
 * The obvious implementation is a line with no product. The server does not
 * allow it: line_items.product_id is NOT NULL with a real foreign key
 * (schema.prisma — `product Product @relation(fields: [productId], ...)`).
 * A line pointing at an id with no row would be accepted locally and then
 * rejected forever on push, which in this codebase means the till keeps
 * working while the sale never reaches the server. That is the exact silent
 * data-loss shape we have been digging out of billing for weeks, so it is not
 * an option.
 *
 * So each store gets ONE hidden product row that every manual line points at,
 * and the line itself carries what actually matters:
 *
 *   line_items.description  the typed name        (already a column)
 *   line_items.hsn_code     the typed HSN/SAC     (already a column)
 *   line_items.gst_rate     the slab for that HSN (already a column)
 *   line_items.unit_price / quantity / taxable_amount / cgst / sgst …
 *
 * Nothing new is stored, nothing new syncs, and the GST summary, GSTR-1 and
 * the printed invoice all read the line, not the product.
 *
 * ── Why the cart uses a different id ────────────────────────────────────────
 * The cart keys lines by product id — add, update quantity, remove. If every
 * manual line carried the placeholder's id, two manual lines would be one line
 * and changing either would change both. So a manual line gets a unique
 * synthetic id for its life in the cart, and {@link resolveManualProductId}
 * swaps in the placeholder at the moment the invoice is written.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { LineItem } from '@/db/models/Billing';
import type { ProductForBilling } from './billingEngine';
import { getHSNByCode, searchHSNByProductName, type HSNEntry } from '@/utils/hsnDatabase';

/**
 * Marks the per-store placeholder. A SKU rather than a new column: adding a
 * column means a migration on four clients and the server, for a row the
 * merchant must never see anyway.
 */
export const MANUAL_ITEM_SKU = '__BIGXBOOK_MANUAL__';

/** What the placeholder is called if it ever does surface somewhere. */
export const MANUAL_ITEM_PRODUCT_NAME = 'Manual entry';

const MANUAL_ID_PREFIX = 'manual:';

/** True for a cart-only id that has no product row behind it. */
export function isManualItemId(id: string): boolean {
  return id.startsWith(MANUAL_ID_PREFIX);
}

function newManualItemId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${MANUAL_ID_PREFIX}${rand}`;
}

export interface ManualItemInput {
  /** What goes on the bill. Required — a line with no description is not one. */
  name: string;
  hsn: string | null;
  /** Already normalised to the store's convention by the caller. */
  rate: number;
  gstRate: number;
  cessRate?: number;
  unit: string;
}

/**
 * A cart line that looks enough like a product for the billing engine.
 *
 * `trackInventory: false` is the load-bearing field: useCreateInvoice skips
 * stock planning for those lines, so nothing tries to find a product row that
 * is not there, and nothing decrements a stock figure that does not exist.
 */
export function buildManualBillingProduct(input: ManualItemInput): ProductForBilling {
  const name = input.name.trim();
  return {
    id: newManualItemId(),
    name,
    sellingPrice: input.rate,
    mrp: 0,
    gstRate: input.gstRate,
    cessRate: input.cessRate ?? 0,
    hsnCode: input.hsn?.trim() || null,
    unit: input.unit || 'PCS',
    // Not stocked, so it can never be the reason a sale is refused for stock.
    currentStock: Number.POSITIVE_INFINITY,
    effectiveStock: Number.POSITIVE_INFINITY,
    isService: false,
    trackInventory: false,
    imageUrl: null,
    categoryId: null,
    barcode: null,
    sku: null,
  };
}

/**
 * The store's placeholder product id, creating the row the first time.
 *
 * Kept inactive so it cannot appear in billing search or the quick-add panel,
 * both of which filter on is_active — the merchant should never be able to put
 * "Manual entry" on a bill by accident.
 */
export async function ensureManualItemProduct(storeId: string): Promise<string> {
  const existing = await database
    .get<Product>('products')
    .query(Q.where('store_id', storeId), Q.where('sku', MANUAL_ITEM_SKU))
    .fetch();
  if (existing.length > 0) return existing[0].id;

  const created = await database.write(async () =>
    database.get<Product>('products').create((p: Product) => {
      p.storeId = storeId;
      p.categoryId = null;
      p.supplierId = null;
      p.name = MANUAL_ITEM_PRODUCT_NAME;
      p.nameHindi = null;
      p.description = 'Carrier row for non-catalog bill lines. Not for sale.';
      p.sku = MANUAL_ITEM_SKU;
      p.barcode = null;
      p.hsnCode = null;
      p.mrp = 0;
      p.sellingPrice = 0;
      p.purchasePrice = 0;
      p.wholesalePrice = null;
      p.taxProfileId = null;
      p.gstRate = 0;
      p.cessRate = 0;
      p.unit = 'PCS';
      p.secondaryUnit = null;
      p.conversionRate = null;
      p.currentStock = 0;
      p.lowStockAlert = 0;
      p.trackInventory = false;
      p.isService = false;
      p.imageUrl = null;
      p.isActive = false;
      p.version = 1;
      p.isSynced = false;
      p.isDeleted = false;
      p.lastSyncCurrentStock = null;
    }),
  );
  return created.id;
}

/**
 * The GST slab for an HSN/SAC, or null when the code is not one we know.
 *
 * Null means "we have nothing to say", not "zero" — a 0% default would quietly
 * under-charge tax on a code that simply is not in the table.
 */
export function gstRateForHsn(code: string): number | null {
  const entry = getHSNByCode(code.trim());
  return entry ? entry.gstRate : null;
}

/** HSN candidates for a typed description, best first. */
export function hsnSuggestionsForName(name: string, max = 5): HSNEntry[] {
  const q = name.trim();
  return q.length < 2 ? [] : searchHSNByProductName(q, max);
}

/**
 * The store's placeholder id if it has one, WITHOUT creating it.
 *
 * Reading must not have side effects: the billing screen asks for the recall
 * list on every keystroke, and a store that has never billed a manual item
 * should not grow a carrier row just because somebody typed in the search box.
 */
export async function findManualItemProduct(storeId: string): Promise<string | null> {
  const rows = await database
    .get<Product>('products')
    .query(Q.where('store_id', storeId), Q.where('sku', MANUAL_ITEM_SKU))
    .fetch();
  return rows.length > 0 ? rows[0].id : null;
}

/** A manual item the store has billed before, ready to be offered again. */
export interface RememberedManualItem {
  name: string;
  hsn: string | null;
  gstRate: number;
  unit: string;
  rate: number;
}

/** How many past manual lines to scan. Comfortably more than a shop's variety. */
const RECALL_SCAN = 400;

/**
 * Manual items this store has billed before, most recently used first.
 *
 * ── Why this reads the bills rather than a table of its own ─────────────────
 * Every manual line already records its name, HSN, slab, unit and rate on
 * line_items, and those rows already sync. Deriving the list from them means a
 * second till knows the shop's manual items the moment a bill reaches it, the
 * list survives signing out, and the sync surface does not grow by a table —
 * which is where this codebase's worst bugs have lived.
 *
 * The cost is that an item is remembered once it has been BILLED, not when it
 * was typed. Adding one to a cart and abandoning the sale leaves nothing
 * behind, which is the right answer anyway: an abandoned sale is not a
 * decision about the shop's catalog.
 */
export async function recentManualItems(
  storeId: string,
  query = '',
  limit = 5,
): Promise<RememberedManualItem[]> {
  const carrierId = await findManualItemProduct(storeId);
  if (!carrierId) return [];

  const rows = await database
    .get<LineItem>('line_items')
    .query(
      Q.where('product_id', carrierId),
      Q.where('is_deleted', false),
      Q.sortBy('created_at', Q.desc),
      Q.take(RECALL_SCAN),
    )
    .fetch();

  const needle = query.trim().toLowerCase();
  const seen = new Map<string, RememberedManualItem>();

  for (const row of rows) {
    const name = row.description?.trim();
    if (!name) continue;
    // Rows arrive newest first, so the first sighting of a name is its most
    // recent use — that is the price and slab to offer back.
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    if (needle && !key.includes(needle)) continue;
    seen.set(key, {
      name,
      hsn: row.hsnCode?.trim() || null,
      gstRate: row.gstRate,
      unit: row.unit || 'PCS',
      rate: row.unitPrice,
    });
    if (seen.size >= limit) break;
  }

  return [...seen.values()];
}
